import { AppPhase, DealingSubPhase } from '../../models/communication/app-enums.model';
import { CardDealtEvent } from '../../models/communication/app-events.model';
import { GameBase } from '../game-base';
import { GameFlowBase } from '../game-flow-base';
import { CardState, SideState } from '../models/game-view.model';

const states = ['START', 'FACE-DOWN-CARD', 'SIDE-SELECTION-ANIMATION', 'REVEAL-CARDS', 'OUTCOME'] as const;

export class OpenGameFlow extends GameFlowBase<GameBase, (typeof states)[number]> {
    private static readonly sideSelectAnimationIntervalMs: number = 330;

    protected override states = states;

    private animationInterval!: ReturnType<typeof setInterval>;

    override onChanges(table?: CardDealtEvent, subPhase?: DealingSubPhase, appPhase?: AppPhase): void {
        switch (this.getState()) {
            case 'START': {
                this.game.faceDownMessage(this.game.getFirstCardPosition());
                this.nextState();
                break;
            }

            case 'FACE-DOWN-CARD': {
                if (subPhase === DealingSubPhase.SideSelectionAnimation) {
                    this.game.updateVisibleCards(CardState.Back);
                    this.startSideSelectAnimation();
                    this.nextState();
                    return;
                }

                if (!table) return;

                this.game.updateCard(table.cardPosition, CardState.Checked);

                if (table.nextCardPosition === undefined) {
                    this.game.allCardsDrawn();
                } else {
                    this.game.faceDownMessage(table.nextCardPosition);
                }

                break;
            }

            case 'SIDE-SELECTION-ANIMATION': {
                if (subPhase === DealingSubPhase.LeftSide || subPhase === DealingSubPhase.RightSide) {
                    this.stopSideSelectAnimation();
                    this.nextState();
                }

                break;
            }

            case 'REVEAL-CARDS': {
                if (appPhase === AppPhase.Decision) {
                    this.game.playerDecisionTime();
                    return;
                }

                if (!table) return;

                this.game.revealCard(table.cardPosition, table.card);
                this.game.showHandValue(table);

                if (table.nextCardPosition !== undefined) {
                    this.game.faceUpMessage(table.nextCardPosition);
                }

                if (table.outcome !== undefined) {
                    this.nextState();
                }

                break;
            }

            case 'OUTCOME': {
                if (table?.outcome !== undefined) {
                    this.game.handleOutcome(table);
                }
            }
        }
    }

    startSideSelectAnimation(): void {
        this.game.updateTitle($localize`Side selection time`, { isSpecial: true });
        this.animationInterval = this.setSideSelectInterval();
    }

    stopSideSelectAnimation(): void {
        clearInterval(this.animationInterval);
    }

    setSideSelectInterval(): ReturnType<typeof setInterval> {
        let sideIndex = 0;
        const { length } = this.game.data.sides;

        const animate = (): void => {
            this.game.data.sides[sideIndex].state = SideState.Current;
            sideIndex = (sideIndex + 1) % length;
            this.game.data.sides[sideIndex].state = SideState.Muted;
        };

        animate();
        return setInterval(animate, OpenGameFlow.sideSelectAnimationIntervalMs);
    }
}
